import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';
import type { StoryListEntry } from '@peacock/sdk-js';

/**
 * Modal dialog that lets editors create a new Story via the
 * POST /v1/spaces/{slug}/stories endpoint.
 *
 * Inputs:
 *  - slug       (URL segment, lowercase + hyphens; empty = homepage)
 *  - parent     (optional; pick from the existing tree via a flat select)
 *  - displayName (used to seed `content.headline`; not a Story column)
 *
 * After successful create, invalidates the stories query and calls
 * onCreated with the new Story so the caller can auto-select it.
 */
export interface CreateStoryDialogProps {
  /** Used to populate the parent picker. */
  existingStories: StoryListEntry[];
  /** Pre-selected parent (the currently-open Story's id). */
  defaultParentId?: number | null;
  onClose(): void;
  onCreated(story: StoryListEntry): void;
}

export function CreateStoryDialog({
  existingStories,
  defaultParentId = null,
  onClose,
  onCreated,
}: CreateStoryDialogProps): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const activeLocale = useAuthStore((s) => s.activeLocale);
  const queryClient = useQueryClient();

  const [slug, setSlug] = useState('');
  const [headline, setHeadline] = useState('');
  const [parentId, setParentId] = useState<number | null>(defaultParentId);

  const mutation = useMutation({
    mutationFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('Not authenticated');
      }
      const api = makeApi(token);
      const result = await api.createStory(spaceSlug, {
        slug: slug.trim().toLowerCase(),
        parent_id: parentId,
        lang: activeLocale,
        content: {
          _component: 'page',
          headline: headline.trim() || slug,
        },
        message: 'Created via admin',
      });
      if (result.status !== 'ok') {
        // Surface the server's error string when available.
        const code =
          result.status === 'error'
            ? result.code === 409
              ? 'A Story with this slug already exists at this path.'
              : `HTTP ${result.code}`
            : result.status;
        throw new Error(code);
      }
      return result.data.story;
    },
    onSuccess: (story) => {
      queryClient.invalidateQueries({ queryKey: ['stories', spaceSlug] });
      onCreated(story);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-story-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
    >
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-md flex-col gap-4 rounded-lg bg-white p-6 shadow-xl"
      >
        <header>
          <h2
            id="create-story-title"
            className="text-lg font-semibold tracking-tight text-zinc-900"
          >
            Neue Story
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            URL = <code className="font-mono">/{buildPathPreview(slug, parentId, existingStories)}</code>
          </p>
        </header>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Slug (URL-Teil)
          </span>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="ueber-uns"
            /* Pattern uses dash-first / dash-last (NOT mid-class) so
               modern browsers' default Unicode-sets (`/v`) regex
               mode parses it. Mid-class `-` triggers
               "Invalid character class" + crashes the React tree
               via the constraint-validation hook. */
            pattern="^[-a-z0-9]*$"
            data-testid="create-story-slug"
            autoFocus
            className="rounded-md border border-zinc-300 px-3 py-1.5 font-mono text-sm focus:border-zinc-900 focus:outline-none"
          />
          <span className="text-[11px] text-zinc-500">
            Kleinbuchstaben, Ziffern, Bindestriche. Leer lassen für die Startseite (=&nbsp;<code>/</code>).
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Überschrift (initial)
          </span>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Über uns"
            data-testid="create-story-headline"
            className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-zinc-900 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Parent (optional)
          </span>
          <select
            value={parentId ?? ''}
            onChange={(e) =>
              setParentId(e.target.value === '' ? null : Number(e.target.value))
            }
            data-testid="create-story-parent"
            className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:border-zinc-900 focus:outline-none"
          >
            <option value="">— Root (keine Eltern-Story) —</option>
            {existingStories.map((s) => (
              <option key={s.id} value={s.id}>
                {s.full_path || '/'}
              </option>
            ))}
          </select>
        </label>

        {mutation.isError ? (
          <p
            role="alert"
            className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
          >
            {(mutation.error as Error).message}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            data-testid="create-story-submit"
            className="rounded bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? 'Wird angelegt…' : 'Story anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Best-effort preview of what the resulting full_path will be —
 * mirrors the server-side derivation in StoryController::store
 * (`parent.full_path + '/' + slug`). Used only in the dialog
 * subtitle for editor feedback; the server is authoritative on save.
 */
function buildPathPreview(
  slug: string,
  parentId: number | null,
  existing: StoryListEntry[],
): string {
  const cleanSlug = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
  if (parentId === null) {
    return cleanSlug;
  }
  const parent = existing.find((s) => s.id === parentId);
  if (parent === undefined) {
    return cleanSlug;
  }
  const parentPath = parent.full_path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (parentPath === '' && cleanSlug === '') return '';
  if (parentPath === '') return cleanSlug;
  return `${parentPath}/${cleanSlug}`;
}
